import React, { useState, useEffect, useRef, Component } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { validateDocumentFile, PDFPreview } from "../utils/pdfConversion";
import useCVAnalysis from "../hooks/useCVAnalysis";
import * as mammoth from 'mammoth'; // For DOCX conversion
import { useTokens } from "../hooks/useTokens";

// Local Error Boundary specifically for PDF Preview to prevent page crashes on tablet
class PDFPreviewErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('PDFPreview error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-center">
          <p className="text-amber-800 text-sm font-medium mb-2">
            Unable to preview PDF on this device
          </p>
          <p className="text-amber-600 text-xs">
            Don't worry - your CV will still be analyzed. The preview feature may not be available on all devices.
          </p>
          <button
            type="button"
            onClick={() => this.setState({ hasError: false, error: null })}
            className="mt-3 text-xs px-3 py-1 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition"
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Helper function to get category tag class
const getCategoryClass = (category) => {
  if (!category) return '';
  
  // Normalize the category name for consistent CSS class handling
  // Remove spaces, slashes, convert to lowercase
  let normalizedCategory = category.toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/\//g, '-')
    .replace(/[^a-z0-9-]/g, '');
  
  // Handle special cases based on your data
  if (normalizedCategory.includes('medical-education')) return 'medical-education';
  if (normalizedCategory.includes('medical-content-writer')) return 'medical-content-writer';
  if (normalizedCategory.includes('telemedicine')) return 'telemedicine';
  if (normalizedCategory.includes('cruise-ship-doctor')) return 'cruise-ship-doctor';
  if (normalizedCategory.includes('business-consulting')) return 'business-consulting';
  if (normalizedCategory.includes('medical-officer')) return 'medical-officer';
  if (normalizedCategory.includes('research-medical-officer')) return 'research-medical-officer';
  if (normalizedCategory.includes('registrar')) return 'registrar';
    
  // Default fallback - return the first part of the category
  return normalizedCategory.split('-')[0];
};

const jobCategories = [
  { id: "medical-education", name: "Medical Education" , isActive: true},
  { id: "business-consulting", name: "Business Consulting" , isActive: true},
  { id: "medical-officer", name: "Clinical Medicine" , isActive: true},
  { id: "medical-content-writer", name: "Medical Content Writer" , isActive: false},
  { id: "telemedicine", name: "Telemedicine" , isActive: false},
  { id: "cruise-ship-doctor", name: "Cruise Ship Doctor" , isActive: false},
  // { id: "research-medical-officer", name: "Research Medical Officer" , isActive: false},
  // { id: "registrar", name: "Registrar" , isActive: false},
];

const countries = [
  { id: "South Africa", name: "South Africa" },
  { id: "United Kingdom", name: "United Kingdom" },
  { id: "United States", name: "United States" },
  // { id: "Canada", name: "Canada" },
  // { id: "Australia", name: "Australia" },
  // { id: "New Zealand", name: "New Zealand" },
  // { id: "Germany", name: "Germany" },
  // { id: "Netherlands", name: "Netherlands" },
];

const CVAnalyserPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState("South Africa");
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [confirmPII, setConfirmPII] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  
  const countryDropdownRef = useRef(null);
  
  // Token management
  const { tokens,  addTokens, deductTokens } = useTokens();
  
  const { 
    loading, 
    error, 
    statusMessage, 
    submitCV
  } = useCVAnalysis(user);

  const requiredTokens = 250; // Each CV analysis requires 250 tokens

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setCountryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle category selection
  const handleCategorySelect = (categoryId) => {
    const category = jobCategories.find(cat => cat.id === categoryId);
    if (category && category.isActive) {
      setSelectedCategory(categoryId);
    }
  };

  // Handle country selection
  const handleCountrySelect = (countryId) => {
    setSelectedCountry(countryId);
  };

  // Handle file selection through input
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const fileObj = e.target.files[0];
      const validation = validateDocumentFile(fileObj);
      
      if (validation.valid) {
        setFile(fileObj);
        setValidationError(null);
        
        if (fileObj.type === 'application/pdf') {
          setPreviewVisible(true);
        } else {
          setPreviewVisible(false);
        }
      } else {
        setValidationError(validation.message);
        setPreviewVisible(false);
      }
    }
  };

  const handleDeleteFile = (e) => {
    e.preventDefault();
    setFile(null);
    setValidationError(null);
    setPreviewVisible(false);
    setDragActive(false);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const fileObj = e.dataTransfer.files[0];
      const validation = validateDocumentFile(fileObj);
      
      if (validation.valid) {
        setFile(fileObj);
        setValidationError(null);
        
        if (fileObj.type === 'application/pdf') {
          setPreviewVisible(true);
        } else {
          setPreviewVisible(false);
        }
      } else {
        setValidationError(validation.message);
        setPreviewVisible(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedCategory) {
      setValidationError("Please select a job category");
      return;
    }

    if (!selectedCountry) {
      setValidationError("Please select a country");
      return;
    }
    
    if (!file) {
      setValidationError("Please upload a CV/resume file");
      return;
    }

    // Check if user has sufficient tokens
    if (tokens < requiredTokens) {
      alert("You do not have enough tokens to analyze your CV. Please purchase more tokens.");
      return;
    }
    
    try {
      await deductTokens(requiredTokens);
      
      const result = await submitCV({
        file,
        jobCategory: selectedCategory,
        country: selectedCountry,
        dependencies: {
          mammoth // Pass mammoth as a dependency for DOCX extraction
        }
      });
      
      if (result && result.success) {
        // Determine the correct route based on current path to preserve sidebar
        const currentPath = location.pathname;
        let resultsRoute = '/cv-analysis-results';
        
        if (currentPath.includes('/employee/')) {
          resultsRoute = '/employee/cv-analysis-results';
        } else if (currentPath.includes('/employer/')) {
          resultsRoute = '/employer/cv-analysis-results';
        }
        
        navigate(`${resultsRoute}?id=${result.requestId}`);
      } else {
        await addTokens(requiredTokens);
        setValidationError("CV analysis failed. Token has been refunded.");
      }
    } catch (tokenError) {
      console.error('Token deduction error:', tokenError);
      setValidationError("Insufficient tokens or token deduction failed.");
    }
  };

  const togglePreview = () => {
    if (file && file.type === 'application/pdf') {
      setPreviewVisible(!previewVisible);
    }
  };

  const displayError = validationError || error;

  return (
    <>
    <style>{`
      @media (max-width: 768px) {
        .cv-analyser-heading {
          font-size: 20px !important;
          white-space: normal !important;
          line-height: 1.4 !important;
        }
      }
      @media (min-width: 768px) and (max-width: 1280px) {
        .cv-analyser-heading {
          font-size: 20px !important;
          white-space: normal !important;
          line-height: 1.4 !important;
        }
      }
    `}</style>
    <div className="min-h-screen bg-[#f7f8fb] px-1 pt-1 pb-8 md:px-4 md:pt-10 flex items-start justify-center">
      <div className="w-full max-w-[820px] rounded-[28px] bg-white px-1 py-7 md:px-6 lg:px-10">
        <div className="mb-6">
          <div className="mb-4 rounded-2xl border border-[#f3e3ff] bg-[#faf2ff] px-3.5 py-2 text-sm font-medium text-[#5f2aa5] text-center md:text-left">
            Your first CV Analyser credit is on us. Spot gaps, fix issues, and stand out.
          </div>

          <h1 className="mb-2 text-center text-[24px] font-bold text-slate-900 whitespace-normal md:whitespace-normal lg:whitespace-nowrap cv-analyser-heading">
            Let us help you shape a CV that reflects your full potential
          </h1>
          
          <p className="text-center text-base text-slate-600">
            Upload your CV for the role you're applying to, and we'll give you tailored, actionable feedback.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
        <div className="rounded-2xl border border-slate-100 bg-white p-4">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.35em] text-slate-500">Step 1</p>
          <h2 className="text-base font-semibold text-slate-900 mb-3">Pick the job category you are applying for</h2>
          <div className="flex flex-wrap gap-2.5">
            {jobCategories.filter(category => category.isActive).map((category) => {
              const categoryClass = getCategoryClass(category.id);
              return (
                <button
                  key={category.id}
                  type="button"
                  className={`py-2 px-4 rounded-full text-sm font-medium transition-colors category-pill ${categoryClass} ${
                    selectedCategory === category.id ? `${categoryClass} active` : ''
                  } hover:bg-opacity-80`}
                  onClick={() => handleCategorySelect(category.id)}
                >
                  {category.name}
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white p-5">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.35em] text-slate-500">Step 2</p>
          <h2 className="text-lg font-semibold text-slate-900 mb-3">Choose the country where you are applying</h2>
          <div className="w-full relative" ref={countryDropdownRef}>
            <button
              type="button"
              onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setCountryDropdownOpen(!countryDropdownOpen);
                }
              }}
              className="w-full p-3.5 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white text-left flex justify-between items-center text-slate-700"
              aria-haspopup="listbox"
              aria-expanded={countryDropdownOpen}
            >
              <span>{selectedCountry}</span>
              <svg 
                className={`w-5 h-5 text-gray-400 transition-transform ${countryDropdownOpen ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {countryDropdownOpen && (
              <div 
                className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-2xl shadow-xl max-h-60 overflow-y-auto"
                role="listbox"
              >
                {countries.map((country) => (
                  <button
                    key={country.id}
                    type="button"
                    className={`w-full px-4 py-2.5 text-left text-sm hover:bg-slate-50 focus:bg-slate-50 focus:outline-none ${
                      selectedCountry === country.id ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-600'
                    }`}
                    onClick={() => {
                      handleCountrySelect(country.id);
                      setCountryDropdownOpen(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCountrySelect(country.id);
                        setCountryDropdownOpen(false);
                      }
                    }}
                    role="option"
                    aria-selected={selectedCountry === country.id}
                  >
                    {country.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white p-5">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.35em] text-slate-500">Step 3</p>
          <h2 className="text-lg font-semibold text-slate-900 mb-3">Upload your CV</h2>
          <div 
            className={`rounded-[20px] border-2 border-dashed p-7 text-center transition ${
              dragActive ? "border-indigo-400 bg-indigo-50" : "border-slate-200 bg-white"
            }`}
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
          >
            <div className="mb-3 flex justify-center">
              <div className="h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
              </div>
            </div>
            <h3 className="text-base font-semibold text-slate-900 mb-2">Choose a file to drag and drop here.</h3>
            <p className="text-sm text-slate-500 mb-4">PDF, Docx or Doc, up to 50mb.</p>
            
            {file ? (
              <div className="mt-4 text-left p-4 bg-slate-50 rounded-2xl border border-slate-100">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium">{file.name}</p>
                    <p className="text-xs text-gray-500">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
                  </div>
                  {file.type === 'application/pdf' && (
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={togglePreview}
                        className="text-xs px-3 py-1 rounded-full bg-slate-900 text-white hover:bg-slate-700 transition"
                      >
                        {previewVisible ? 'Hide preview' : 'Show preview'}
                      </button>

                      <button
                        type="button"
                        onClick={(e) => handleDeleteFile(e)}
                        className="text-red-500 hover:text-red-700"
                        title="Delete file"
                      >
                        <img src="/Trash 2.svg" alt="Delete" className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-4">
                <label className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-slate-900/10 transition hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2 cursor-pointer">
                  Browse files
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                  />
                </label>
              </div>
            )}
          </div>
        </div>

        {/* PDF Preview */}
        {file && file.type === 'application/pdf' && previewVisible && (
          <div className="mt-6 border border-gray-200 rounded-xl p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-medium">Document Preview</h3>
              <button
                type="button"
                onClick={() => setPreviewVisible(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="max-h-96 overflow-y-auto">
              <PDFPreviewErrorBoundary>
                <PDFPreview file={file} maxPages={3} />
              </PDFPreviewErrorBoundary>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              We'll extract the text from your PDF for analysis. The preview helps ensure the document is correctly formatted.
            </p>
          </div>
        )}

        {displayError && (
          <div className="mt-4 text-red-600 text-sm text-center">{displayError}</div>
        )}

        {statusMessage && (
          <div className="mt-4">
            <div className="animate-pulse flex space-x-4 items-center">
              <div className="rounded-full bg-indigo-400 h-3 w-3"></div>
              <div className="h-4 bg-indigo-200 rounded w-full max-w-md"></div>
            </div>
            <p className="text-sm text-indigo-700 mt-2">{statusMessage}</p>
          </div>
        )}

        <div className="mt-2 text-center">

          <div className="flex items-center space-x-4 mb-5">
            <input
              type="checkbox"
              checked={confirmPII}
              onChange={() => setConfirmPII(!confirmPII)}
              id="confirm-pii"
              className="w-6 h-6 border-2 border-gray-300 bg-white appearance-none checked:border-[#061B16] relative"
              style={{ position: "relative" }}
            />

            <label htmlFor="confirm-pii" className="text-sm text-slate-600 leading-snug text-left flex-1 relative">
            I understand that I should remove any personally identifiable information (emails, phone numbers, addresses, etc.)
            from my CV before uploading to protect my privacy. While we do not store your CV, removing this sensitive data ensures it is never sent to external services.
            </label>
          </div>

          <button
            type="submit"
            disabled={loading || !file || !selectedCategory || !selectedCountry || !confirmPII}
            className={`px-8 py-3 rounded-full text-white font-semibold shadow-lg shadow-slate-900/10 transition ${
              loading || !file || !selectedCategory || !selectedCountry || !confirmPII
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-slate-900 hover:bg-slate-700"
            }`}
          >
            {loading ? "Processing..." : "Analyse CV"}
          </button>
        </div>
        </form>
      </div>
    </div>
    </>
  );
};

export default CVAnalyserPage;